/**
 * The columns a "Row changed" trigger watches: "All columns" (an empty list)
 * or a choice of the source table's columns. The status tracking column is
 * listed but never pickable: the automation writes it itself, so watching it
 * would start the automation again. Shared rule: assertWatchedColumns.
 */
import { Checkbox } from '@affine/component';
import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { useI18n } from '@affine/i18n';
import type { FC } from 'react';
import { useCallback } from 'react';

import type { ColumnInfo } from '../../services/database-discovery';
import { SectionField } from './section-layout';
import * as styles from './watched-columns-field.css';

export interface WatchedColumnsFieldProps {
  /** The source table's columns. */
  columns: readonly ColumnInfo[];
  /** The watched list; empty watches every column but the status column. */
  value: readonly string[];
  /** The column status tracking writes, if any. */
  statusColumnId: string | null;
  onChange: (watchedColumnIds: string[]) => void;
  disabled?: boolean;
}

export const WatchedColumnsField: FC<WatchedColumnsFieldProps> = ({
  columns,
  value,
  statusColumnId,
  onChange,
  disabled = false,
}) => {
  const t = useI18n();
  const all = value.length === 0;
  const statusColumn = columns.find(column => column.id === statusColumnId);
  const pickable = columns.filter(column => column.id !== statusColumnId);

  const toggleAll = useCallback(() => {
    if (disabled) return;
    // Leaving "All columns" starts from every pickable column, so nothing
    // stops being watched by the switch itself.
    onChange(all ? pickable.map(column => column.id) : []);
  }, [all, disabled, onChange, pickable]);

  const toggleColumn = useCallback(
    (columnId: string) => {
      if (disabled || columnId === statusColumnId) return;
      const next = value.includes(columnId)
        ? value.filter(id => id !== columnId)
        : [...value, columnId];
      // Unticking the last column means no choice, which is "All columns".
      onChange(next);
    },
    [disabled, onChange, statusColumnId, value]
  );

  return (
    <SectionField
      label={t['com.streamlain.automation.sidebar.trigger.watched-columns']()}
    >
      <div className={styles.list} data-testid="watched-columns">
        <label className={styles.row} data-disabled={disabled || undefined}>
          <Checkbox
            checked={all}
            disabled={disabled}
            onChange={toggleAll}
            data-testid="watched-columns-all"
          />
          <span className={styles.name}>
            {statusColumn
              ? t[
                  'com.streamlain.automation.sidebar.trigger.watched-all-except'
                ]({ name: statusColumn.name })
              : t['com.streamlain.automation.sidebar.trigger.watched-all']()}
          </span>
        </label>
        <AnimatedRegion expanded={!all} keepMounted>
          <div className={styles.columns}>
            {columns.map(column => {
              const isStatus = column.id === statusColumnId;
              return (
                <label
                  key={column.id}
                  className={styles.row}
                  data-disabled={disabled || isStatus || undefined}
                  data-watched-column={column.id}
                >
                  <Checkbox
                    checked={!isStatus && value.includes(column.id)}
                    disabled={disabled || isStatus}
                    onChange={() => toggleColumn(column.id)}
                  />
                  <span className={styles.name}>
                    {column.name}
                    {isStatus && (
                      <span className={styles.note}>
                        {t[
                          'com.streamlain.automation.sidebar.trigger.watched-status-column'
                        ]()}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </AnimatedRegion>
      </div>
    </SectionField>
  );
};
